import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSRichText from '@/components/ui/PSRichText';

/**
 * ── /components/ps-rich-text ─────────────────────────────────────────────
 *
 * PSRichText — renders stored MARKDOWN copy as tokened native text via
 * `marked` + `react-native-render-html` (see the component's own docstring
 * for the full token-bridge/font-face rationale). Real call sites: event
 * detail's description and terms (`app/events/[id]/index.tsx`), TicketTier's
 * per-tier description (`components/TicketTier.tsx`), checkout/bookings/
 * requests' terms blocks (`app/events/[id]/confirm.tsx`,
 * `app/bookings/[id]/index.tsx`, `app/requests/[id]/index.tsx`), the FAQ
 * ledger's answers (`components/FaqLedger.tsx`, `surface="dark"`), and the
 * static privacy-policy/terms-and-conditions pages.
 *
 * NONE of those wrap it in a border. The old `all.tsx` holding-pen preview
 * put "what's included" and "terms & conditions" inside a hairline-border +
 * `$base`-radius + `$md`-padding box and called it "locked style props" —
 * but grepping every real call site turns up no such box anywhere: the event
 * detail page wraps description/terms in a bare `YStack` + `PSText.H1`, the
 * checkout/booking/request screens use the unboxed `Section` molecule
 * (`components/Section.tsx` — header + gap, no border), and TicketTier
 * doesn't head it at all (just an opacity wrapper for the sold-out dim). "AP
 * what's included" isn't a real heading anywhere either — it only ever
 * appeared in PSRichText's own docstring as an example field name. So this
 * preview drops that fictional box entirely and captions each block with
 * what actually wraps it on the real screen instead.
 *
 * Fixtures below cover every tag PSRichText tokens (`STATIC_TAGS_STYLES` in
 * the component): p/strong-b/em-i/ul/ol/li, and all six heading levels
 * (h1/h2 get distinct sizes, h3–h6 collapse to the same body size — the old
 * kitchen-sink fixture only ever exercised h2, so the heading-scale block
 * below is new). Tables, code fences, and blockquotes are deliberately
 * skipped: the component's docstring says they fall through to the
 * library's own un-tokened defaults on purpose, so there's nothing of ours
 * to preview.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Flush-left in the template literals on purpose: four leading spaces would
// make markdown read the line as a code block.

// A real production event description — the plain-prose case with no lists
// or headings, just paragraphs and one bold phrase per paragraph.
const PREVIEW_DESCRIPTION = `Get your hands into dough in a **guided pizza-making experience** with our chef. From stretching the dough to filling, folding and sealing it just right, you’ll be guided through each step of the process before baking your pizza to golden perfection.

This session is about slowing down, working with your hands, and enjoying the simple satisfaction of making something from scratch.

Head to our **Kitchen Studio on the first floor** once you arrive at the venue.`;

// Exercises every element PSRichText styles except the heading scale (see
// PREVIEW_HEADINGS below): bold, italic, both, ul, ol, a heading, and a link
// that opens via `openURL` (the wrapper the house rule requires — the
// library's own default handler calls `Linking.openURL` directly and throws
// on an unopenable URL).
const PREVIEW_KITCHEN_SINK = `This description contains every element we support.

## A section heading

We support:

- **Bold text**
- *Italic text*
- ***Bold and italic***

we also have ordered lists:

1. like
2. so

And [a link](https://puttingscene.com) that opens in the browser.`;

// A real terms-and-conditions body — short, bold for the one load-bearing
// word, otherwise plain prose.
const PREVIEW_TERMS = `Tickets are **non-refundable** and non-transferable.

Please arrive at least fifteen minutes before your slot. Latecomers may not be admitted.`;

// h1/h2 get their own sizes ($large / $md); h3 down all share $base and
// never shrink further (see the component's comment on why). The kitchen
// sink above only ever hits h2 — this is the one place all three tiers sit
// side by side so the ladder is checkable at a glance. No authored copy
// currently reaches for h1 or h4–h6, but the styles exist and are worth
// pinning.
const PREVIEW_HEADINGS = `# Top-level heading — $large

## Section heading — $md

### Sub-heading — $base, same size as h4/h5/h6 below it`;

// FaqLedger's answer copy (components/FaqLedger.tsx) — short, one bold
// phrase and a link, on the Access Pass landing page's actual $brandDark
// ground, which is why surface="dark" exists at all.
const PREVIEW_DARK = `Your ticket is **fully refundable** up to 24 hours before the event — see our [refund policy](https://puttingscene.com) for details.`;

export default function PSRichTextPreview() {
  return (
    <>
      <Head>
        <title>PSRichText · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Real production copy, unboxed — this is exactly how
            events/[id]/index.tsx's "About" section wraps it: a bare
            YStack + PSText.H1 heading, no border (see the file docstring
            above for why this preview doesn't add one). */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              description — event detail&apos;s &quot;About&quot; section, plain
              prose + inline bold, no lists or headings
            </Text>
            <PSRichText markdown={PREVIEW_DESCRIPTION} />
          </YStack>

          {/* Kitchen sink — bold/italic/both, ul, ol, a heading, and a link.
            Grouped together because it's the fastest way to eyeball every
            tokened tag at once; no single real call site uses all of these
            in one body, but each element individually appears in authored
            copy somewhere in the three call sites above. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              kitchen sink — bold, italic, bold+italic, unordered + ordered
              lists, a heading, and a link (opens via openURL, not raw Linking)
            </Text>
            <PSRichText markdown={PREVIEW_KITCHEN_SINK} />
          </YStack>

          {/* Heading scale — h1 vs h2 vs h3 (h4-h6 render identically to h3,
            so a fourth block would show nothing new). */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              heading scale — h1 ($large) and h2 ($md) get distinct sizes, h3
              down all collapse to $base
            </Text>
            <PSRichText markdown={PREVIEW_HEADINGS} />
          </YStack>

          {/* Terms & conditions copy — captioned with what actually wraps it
            on each real screen, since none of them agree on the same
            wrapper and none of them use a border (see the file docstring). */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              terms & conditions copy — on events/[id]/index.tsx this sits in a
              bare YStack + PSText.H1; on confirm/bookings/requests it sits in
              the unboxed Section molecule instead. Neither borders it.
            </Text>
            <PSRichText markdown={PREVIEW_TERMS} />
          </YStack>

          {/* surface="dark" — FaqLedger's FAQ answers on the Access Pass
            landing page's real $brandDark ground. Standing in here the same
            way ps-button.tsx's toolbar swatch stands in for its own
            transparent-header backdrop: this IS the real surface, not an
            approximation of one. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$sm" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              surface=&quot;dark&quot; — FaqLedger&apos;s answers, drawn on the
              Access Pass landing page&apos;s actual $brandDark ground
            </Text>
            <PSRichText markdown={PREVIEW_DARK} surface="dark" />
          </YStack>

          {/* Empty markdown — the falsy guard in PSRichText's `source` memo.
            `marked.parse` THROWS on null/undefined, and the wire can omit an
            optional body (terms especially, see confirm.tsx/bookings/[id]'s
            `event.termsAndConditions &&` guards around their own Section).
            This renders nothing rather than taking the screen down; there is
            deliberately nothing visible below the caption. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              empty markdown — renders nothing, doesn&apos;t crash (the wire can
              omit an optional body; nothing should appear below this line)
            </Text>
            <PSRichText markdown="" />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
